import { FC, Dispatch, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'
import { TWebsiteFormValues } from '../../types'
import styles from './Form.module.scss'
import { useNavigate } from 'react-router-dom';

type TWebsiteForm = {
    title: string
    setData: Dispatch<React.SetStateAction<TWebsiteFormValues>>
    toggleResetAllForms: boolean
    WEBSITE_FORM_DATA: TWebsiteFormValues
}

export const WebsiteForm: FC<TWebsiteForm> = ({ title, setData, WEBSITE_FORM_DATA, toggleResetAllForms }) => {
    const { register, handleSubmit, watch, formState: { errors }, setValue, reset, control } = useForm<TWebsiteFormValues>({ defaultValues: WEBSITE_FORM_DATA }) 
    const { monthlyTraffic, conversionRate, qualifiedLeads, clothingRate } = watch()
    const windowWidth = window.innerWidth
    let navigate = useNavigate();

    const onBlur = (data: TWebsiteFormValues) => {
        setData(data)
    }
    useEffect(() => {
        setValue("qualifiedLeads", Math.floor(monthlyTraffic * (conversionRate / 100)))
        setValue("customers", Math.floor(qualifiedLeads / (100 / clothingRate)))
    }, [monthlyTraffic, conversionRate, clothingRate, qualifiedLeads, setValue])

    useEffect(() => {
        navigate('/')
        reset()
        setData(WEBSITE_FORM_DATA)
    }, [toggleResetAllForms, reset, WEBSITE_FORM_DATA, setData])


    return (
        <div className={styles.formContainer}>
            <h3 className={styles.formTitle}>{title}</h3>
            <form className={styles.form} onBlur={handleSubmit(onBlur)}>
                <div className={styles.formGroup} >
                    <label htmlFor="monthlyTraffic">Monthly traffic</label>
                    <Controller
                        name='monthlyTraffic'
                        control={control}
                        render={({ field: { onBlur, name, value } }) => (
                            <NumericFormat
                                thousandSeparator={true}
                                onValueChange={(v) => {
                                    setValue("monthlyTraffic", Number(v.value))
                                }}
                                defaultValue={WEBSITE_FORM_DATA.monthlyTraffic}
                                name={name}
                                value={value}
                                onBlur={onBlur}
                            />
                        )}
                    />

                </div>
                <div className={styles.column}>
                    <div className={styles.formGroup}>
                        <label htmlFor="conversion-rate">Conversion rate</label>
                        <Controller
                            name='conversionRate'
                            control={control}
                            rules={{ max: 100 }}
                            render={({ field: { onBlur, name, value } }) => (
                                <NumericFormat
                                    thousandSeparator={true}
                                    suffix='%'
                                    onValueChange={(v) => {
                                        setValue("conversionRate", Number(v.value))
                                    }}
                                    defaultValue={WEBSITE_FORM_DATA.conversionRate}
                                    name={name}
                                    value={value}
                                    onBlur={onBlur}
                                    max={100}
                                    isAllowed={({ value }) => +value <= 100 && +value >= 0 ? true : false}
                                />
                            )}
                        />
                        {errors.conversionRate ? <>err</> : null}
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="qualified-leads">Qualified leads</label>
                        <input id="qualified-leads" readOnly type='number' 
                            {...register("qualifiedLeads", { required: true, pattern: /\d/g })} />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="closing-rate">Closing rate</label>

                        <Controller
                            name='clothingRate'
                            control={control}
                            render={({ field: { onBlur, name, value } }) => (
                                <NumericFormat
                                    thousandSeparator={true}
                                    suffix='%'
                                    onValueChange={(v) => {
                                        setValue("clothingRate", Number(v.value))

                                    }}
                                    defaultValue={WEBSITE_FORM_DATA.clothingRate}
                                    name={name}
                                    value={value}
                                    onBlur={onBlur}
                                    isAllowed={({ value }) => +value <= 100 && +value >= 0 ? true : false}
                                />
                            )}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="customers">Customers</label>
                        <input id="customers" type='number' readOnly
                            {...register("customers", { required: true, pattern: /\d/g })} />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="avg-mrr">Avg MRR per customer</label>
                        <Controller
                            name='avgMRR'
                            control={control}
                            render={({ field: { onBlur, name, value } }) => (
                                <NumericFormat
                                    thousandSeparator={true}
                                    prefix='$'
                                    onValueChange={(v) => {
                                        setValue("avgMRR", Number(v.value))

                                    }}
                                    defaultValue={WEBSITE_FORM_DATA.avgMRR}
                                    name={name}
                                    value={value}
                                    onBlur={onBlur}
                                />
                            )}
                        />
                    </div>
                    <div className={`${styles.formGroup} ${styles.retention}`}>
                        <label htmlFor="avg-retention">Average retention time</label>
                        <input id="avg-retention" type='number' 
                            {...register("retention", { required: true, pattern: /\d/g })}>
                        </input>
                        {windowWidth > 950 && <span className={styles.retentionMonth}>Months</span>}
                    </div>

                </div>
            </form>
        </div>
    )
}
